import { Button } from '@common/components/button';
import Card from '@common/components/card';
import { Container } from '@common/components/container';
import { Toggle } from '@common/components/form/inputs/toggle';
import Icon from '@common/components/icon';
import { TopNavigationBar } from '@common/components/navigation-bar';
import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { ESearchPredicateTypes } from '@common/definitions';
import { AudienceInputHookForm } from '@common/components/form/audience';
import { useForm, useWatch } from 'react-hook-form';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import { OverviewSection } from '@common/components/overview';
import { FormIcon } from '@modules/forms/components/form-icon';
import Api, { useRequest } from '@common/services/api';
import { useAppSelector } from '@common/hooks';
import { selected } from '@modules/organisation/selectors/organisation';
import { DateInputHookForm } from '@common/components/form';
import { AlertService } from '@common/services/alert';
import { predicatesToPayload } from '@common/utils/predicates/predicates';
import Profile from '@common/components/profile';
import SearchBar from '@common/components/search-bar';
import Spinner from '@common/components/spinner';

const BulkEvaluations = () => {
  const { t } = useTranslation();
  const { networkId } = useParams<{ networkId: string }>();
  const organisation = useAppSelector(selected);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showReviewerModal, setShowReviewerModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [selectedReviewer, setSelectedReviewer] = useState<any>(null);
  const [hasDeadline, setHasDeadline] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewerSearchQuery, setReviewerSearchQuery] = useState<string>('');
  const basePath = networkId ? `/networks/${networkId}` : '/admin';
  const history = useHistory();

  const { data: evaluationFormsData } = useRequest(
    `/v1/organisations/${organisation.id}/performance/forms`
  );

  const { data: adminsData, loading: isLoadingAdmins } = useRequest(
    `/v3/organisations/${organisation.id}/admins`
  );

  const { control } = useForm<{
    audience: { predicate_type: ESearchPredicateTypes; predicates: any[] };
    deadline?: string;
  }>({
    defaultValues: {
      audience: {
        predicate_type: ESearchPredicateTypes.MATCH_ANY,
        predicates: [],
      },
    },
  });

  const formAudience = useWatch({
    control,
    name: 'audience',
  }) || { predicate_type: ESearchPredicateTypes.MATCH_ANY, predicates: [] };

  const deadline = useWatch({
    control,
    name: 'deadline',
  });

  // Filter admins based on search query
  const filteredAdmins = useMemo(() => {
    if (!adminsData || !reviewerSearchQuery.trim()) {
      return adminsData || [];
    }

    const query = reviewerSearchQuery.toLowerCase().trim();
    return adminsData.filter((admin: any) => {
      const fullName = admin.full_name?.toLowerCase() || '';
      const email = admin.email?.toLowerCase() || '';
      const firstName = admin.first_name?.toLowerCase() || '';
      const lastName = admin.last_name?.toLowerCase() || '';
      const username = admin.username?.toLowerCase() || '';

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        firstName.includes(query) ||
        lastName.includes(query) ||
        username.includes(query)
      );
    });
  }, [adminsData, reviewerSearchQuery]);

  const handleCreateBulkEvaluations = useCallback(async () => {
    if (!selectedForm) {
      AlertService.warning(t('performance:please_select_form'));
      return;
    }

    if (!selectedReviewer) {
      AlertService.warning(
        t('performance:please_select_reviewer') || 'Please select a reviewer'
      );
      return;
    }

    if (!formAudience?.predicates || formAudience.predicates.length === 0) {
      AlertService.warning(t('performance:please_select_audience'));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        predicates: predicatesToPayload(formAudience.predicates),
        predicateType:
          formAudience.predicate_type || ESearchPredicateTypes.MATCH_ANY,
        reviewerIds: [selectedReviewer.id],
        deadlineAt: hasDeadline && deadline ? deadline : null,
      };

      await Api.post<{ data: any[] }>(
        `/v1/organisations/${organisation.id}/performance/forms/${selectedForm.id}/evaluations/bulk`,
        payload
      );

      AlertService.success(t('performance:bulk_evaluations_created'));

      // Reset form
      setSelectedForm(null);
      setSelectedReviewer(null);
      setHasDeadline(false);
      history.push(`${basePath}/performance/evaluations`);
    } catch (error: any) {
      AlertService.forStatus(error?.status_code, {
        warning: t('performance:bulk_evaluations_create_warning'),
        error: t('performance:bulk_evaluations_create_error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedForm,
    selectedReviewer,
    formAudience,
    hasDeadline,
    deadline,
    organisation.id,
    t,
    basePath,
    history,
  ]);

  return (
    <>
      <Container name="Bulk Evaluations">
        <TopNavigationBar
          breadcrumbs={[
            {
              name: t('performance:evaluations'),
              path: `${basePath}/performance/evaluations`,
            },
            {
              name: t('performance:bulk_evaluations'),
            },
          ]}
          title={t('performance:bulk_evaluations')}
          action={
            <>
              <Button
                type="default"
                size="large"
                onClick={() => history.goBack()}
              >
                {t('performance:go_back')}
              </Button>
              <Button
                iconRight="start_evaluation"
                type="primary"
                iconSize={14}
                className="!tw-gap-2"
                onClick={handleCreateBulkEvaluations}
                disabled={isSubmitting || !selectedForm}
                isLoading={isSubmitting}
              >
                {t('performance:create_evaluation')}
              </Button>
            </>
          }
        />
        <Container.Content className="!tw-max-w-[40%] tw-gap-6 tw-flex tw-flex-col">
          <Card>
            <Card.Content className="tw-flex tw-flex-col tw-gap-5 tw-p-8">
              <div className="tw-flex tw-flex-col tw-gap-[6px]">
                <div className="tw-text-xl tw-font-semibold tw-text-gray-textDark">
                  {t('performance:form')}
                </div>
                <div className="tw-text-base tw-font-medium tw-text-gray-muted">
                  {t('performance:form_description')}
                </div>
              </div>
              {selectedForm ? (
                <OverviewSection
                  key={selectedForm.id}
                  className="tw-flex tw-flex-row tw-items-center tw-gap-3 !tw-p-4
                  tw-rounded-md tw-cursor-pointer tw-justify-between !tw-mt-0"
                  onClick={() => setShowEvaluationModal(true)}
                >
                  <div className="tw-flex tw-flex-row tw-items-center tw-gap-3 FormTitle">
                    <FormIcon
                      form={selectedForm}
                      iconClassName="!tw-flex-none !tw-mr-0"
                    />
                    <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base tw-font-inter">
                      {selectedForm.title}
                    </div>
                  </div>
                  <Icon
                    type="close"
                    size={30}
                    className="tw-text-gray-muted"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setSelectedForm(null);
                    }}
                  />
                </OverviewSection>
              ) : (
                <div
                  className="tw-bg-gray-custom-50 tw-rounded-lg tw-border tw-flex tw-items-center
            tw-justify-center tw-border-gray-border tw-border-dashed tw-py-[10px]"
                >
                  <Button
                    type="transparent"
                    size="large"
                    icon="add"
                    iconSize={16}
                    className="!tw-p-0"
                    onClick={() => setShowEvaluationModal(true)}
                  >
                    {t('performance:add_form')}
                  </Button>
                </div>
              )}
              <div className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                <Toggle
                  value={hasDeadline}
                  onChange={(value) => setHasDeadline(value)}
                  label="Add a deadline"
                />
                <Icon type="info" size={16} className="tw-text-gray-muted" />
              </div>
              {hasDeadline && (
                <div className="tw-flex tw-flex-col tw-bg-gray-custom-50 tw-rounded-lg tw-p-4 tw-gap-1">
                  <div className="tw-text-sm-plus tw-font-normal tw-text-gray-text">
                    {t('performance:deadline_date')}
                  </div>
                  <DateInputHookForm
                    control={control}
                    name="deadline"
                    placeholder={t('performance:select_a_date')}
                  />
                </div>
              )}
            </Card.Content>
          </Card>
          <Card>
            <Card.Content className="tw-flex tw-flex-col tw-gap-5 tw-p-8">
              <div className="tw-flex tw-flex-col tw-gap-[6px]">
                <div className="tw-text-xl tw-font-semibold tw-text-gray-textDark">
                  {t('performance:reviewer')}
                </div>
                <div className="tw-text-base tw-font-medium tw-text-gray-muted">
                  {t('performance:reviewer_description')}
                </div>
              </div>

              {selectedReviewer ? (
                <OverviewSection
                  key={selectedReviewer.id}
                  className="tw-flex tw-flex-row tw-items-center tw-gap-3 !tw-p-4
                  tw-rounded-md tw-cursor-pointer tw-justify-between !tw-mt-0"
                  onClick={() => setShowReviewerModal(true)}
                >
                  <Profile user={selectedReviewer} size={20} />
                  <Icon
                    type="close"
                    size={30}
                    className="tw-text-gray-muted"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setSelectedReviewer(null);
                    }}
                  />
                </OverviewSection>
              ) : (
                <div
                  className="tw-bg-gray-custom-50 tw-rounded-lg tw-border tw-flex tw-items-center
            tw-justify-center tw-border-gray-border tw-border-dashed tw-py-[10px]"
                >
                  <Button
                    type="transparent"
                    size="large"
                    icon="add"
                    iconSize={16}
                    className="!tw-p-0"
                    onClick={() => setShowReviewerModal(true)}
                  >
                    {t('performance:select_a_reviewer')}
                  </Button>
                </div>
              )}
            </Card.Content>
          </Card>
          <Card>
            <Card.Content className="tw-flex tw-flex-col tw-gap-5 tw-p-8">
              <div className="tw-flex tw-flex-col tw-gap-[6px]">
                <div className="tw-text-xl tw-font-semibold tw-text-gray-textDark">
                  {t('performance:assign_to')}
                </div>
                <div className="tw-text-base tw-font-medium tw-text-gray-muted">
                  {t('performance:assign_to_description')}
                </div>
              </div>
              <AudienceInputHookForm
                name="audience"
                control={control}
                tooltips={{}}
              />
            </Card.Content>
          </Card>
        </Container.Content>
      </Container>
      {showEvaluationModal && (
        <ModalWindow
          show={showEvaluationModal}
          size="small"
          onHide={() => setShowEvaluationModal(false)}
          className="EditableModule"
        >
          <ModalContent
            title={t('performance:select_evaluation')}
            onHide={() => setShowEvaluationModal(false)}
            hideConfirm
            hideFooter
          >
            {evaluationFormsData?.map(
              (row: any) => row.status === 'live' && (
              <OverviewSection
                key={row.id}
                className="tw-flex tw-flex-row tw-items-center tw-gap-3
            hover:tw-bg-gray-100 tw-p-2 tw-rounded-md tw-cursor-pointer"
                onClick={() => {
                      setSelectedForm(row);
                      setShowEvaluationModal(false);
                    }}
              >
                <FormIcon form={row} />
                <div className="tw-font-medium tw-text-gray-textDarkest tw-text-base tw-font-inter">
                  {row.title}
                </div>
              </OverviewSection>
                )
            )}
          </ModalContent>
        </ModalWindow>
      )}
      {showReviewerModal && (
        <ModalWindow
          show={showReviewerModal}
          size="small"
          onHide={() => {
            setShowReviewerModal(false);
            setReviewerSearchQuery('');
          }}
          className="EditableModule"
        >
          <ModalContent
            title={t('performance:select_a_reviewer')}
            onHide={() => {
              setShowReviewerModal(false);
              setReviewerSearchQuery('');
            }}
            hideConfirm
            hideFooter
          >
            <div className="tw-flex tw-flex-col tw-gap-1">
              <SearchBar
                onSearch={(query: string | null) => {
                  setReviewerSearchQuery(query || '');
                }}
                placeholder={t('common:search_bar_placeholder')}
                clearable
              />
              {isLoadingAdmins ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
                  <Spinner centered />
                </div>
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-1">
                  {filteredAdmins.length > 0 ? (
                    filteredAdmins.map((row: any) => (
                      <div
                        key={row.id}
                        className="tw-flex tw-flex-row tw-items-center tw-gap-3
            hover:tw-bg-gray-100 !tw-p-2 tw-rounded-md tw-cursor-pointer"
                        onClick={() => {
                          setSelectedReviewer(row);
                          setShowReviewerModal(false);
                          setReviewerSearchQuery('');
                        }}
                      >
                        <Profile user={row} size={20} />
                      </div>
                    ))
                  ) : (
                    <div className="tw-text-center tw-text-gray-muted tw-py-4">
                      {t('performance:no_reviewers_found')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </ModalContent>
        </ModalWindow>
      )}
    </>
  );
};

export default BulkEvaluations;
